/**
 * Citizen reports — the model, and the confidentiality boundary inside it.
 *
 * ===========================================================================
 * THIS FILE USED TO SAY THE OPPOSITE
 * ===========================================================================
 * Until now the portal took no name, no telephone number and no CNIC, on the
 * principle that the only way to be certain a whistleblower cannot be exposed
 * is to hold nothing that identifies one. Bot protection carried the whole
 * weight of keeping fabricated reports out.
 *
 * The Department has decided differently, and has given a reason that answers
 * the old one: a Turnstile token stops a script, but it does nothing about a
 * person who sits down and files twenty invented reports, each of which costs
 * a Sub-Divisional Officer a site visit. Identity plus a telephone call is the
 * control that actually bites. So a report now REQUIRES the reporter's name,
 * father's name, CNIC, telephone number, address, and a video link — and
 * without the video link there is no submission at all.
 *
 * That trade has a cost, and it should be named rather than glossed. Water
 * theft in a village is often by people with local standing. A farmer at the
 * tail of a canal reporting the man at the head, with their CNIC attached, is
 * taking a risk they did not take before, and some of them will now not
 * report. The design below is what the Department is offering in exchange.
 *
 * ===========================================================================
 * THE BOUNDARY: WHAT CROSSES OUT OF THE CELL, AND WHAT DOES NOT
 * ===========================================================================
 * A report is TWO documents, not one.
 *
 *   public_reports/{id}              the TICKET — de-identified
 *   public_reports/{id}/reporter/…   the IDENTITY — Cell and Secretary only
 *
 * The ticket carries the ticket number, where the theft is, what the Cell
 * attested the video showed, and the state of the chain. The identity document
 * carries the name, father's name, CNIC, telephone number, address and the
 * video link itself.
 *
 * THE VIDEO LINK IS ON THE CONFIDENTIAL SIDE, and that is not an oversight.
 * A video of a canal bank shows the surroundings, often the filmer's voice,
 * sometimes their shadow or their hands. An SDO posted in that sub-division
 * can identify who filmed it. Withholding the name while forwarding the video
 * would be a promise that the footage itself breaks.
 *
 * What travels instead is the Cell member's ATTESTATION of what the video
 * showed. That is the Cell's own work product rather than the citizen's data,
 * and video.ts already argues that an officer's written attestation is the
 * only durable evidence here anyway, because a culprit can delete the post.
 *
 * ===========================================================================
 * WHY A SUBCOLLECTION AND NOT A SET OF HIDDEN FIELDS
 * ===========================================================================
 * Firestore has NO field-level read security. A rule allows or refuses a whole
 * document; it cannot hand a Chief Engineer a report with the CNIC stripped
 * out. Had identity lived on the ticket, every officer who could read the
 * ticket would read the CNIC, and the only thing standing between a reporter
 * and the man they reported would be a React component choosing not to render
 * a field — which is not a control at all, because the data is already in the
 * browser.
 *
 * A subcollection is therefore not a tidying choice. It is the only shape in
 * which this rule can be written.
 */
import { isWithinPunjab } from './geo.js';
import { isValidCnicFormat, isValidPhone, normaliseCnic, normalisePhone } from './cnic.js';
import { parseVideoUrl } from './video.js';
export const MAX_NOTES_LENGTH = 1000;
export const MAX_LOCATION_HINT_LENGTH = 200;
export const MAX_NAME_LENGTH = 100;
export const MAX_ADDRESS_LENGTH = 300;
export const MIN_NAME_LENGTH = 3;
export const MIN_ADDRESS_LENGTH = 10;
/**
 * Field names that must NEVER appear on a TICKET.
 *
 * The old version of this list protected a stronger promise — that nothing
 * identifying was stored anywhere. That promise is gone, and pretending
 * otherwise would be worse than dropping it. What survives is the boundary
 * that still holds: these fields may exist in the reporter subcollection and
 * must never appear on the document the field officers can read.
 *
 * A test fails the build if one ever does, because the failure mode is silent
 * — somebody adds `reporterPhone` to the ticket for a convenience on the Cell
 * screen, and every SDO in Punjab can read it.
 */
export const FORBIDDEN_TICKET_FIELDS = [
    'name',
    'reporterName',
    'fatherName',
    'reporterFatherName',
    'phone',
    'reporterPhone',
    'email',
    'cnic',
    'reporterCnic',
    'cnicHash',
    'address',
    'reporterAddress',
    // The video is on the confidential side: the footage identifies the filmer.
    'videoUrl',
    'videoId',
    'platform',
    // The citizen's own words, which can identify them without their meaning to.
    'notes',
    // Never collected at all, from either side.
    'ip',
    'ipAddress',
    'clientIp',
    'cfConnectingIp',
    'userAgent',
    'ua',
    'country',
    'city',
    'region',
    'timezone',
    'asn',
    'colo',
    'sessionId',
    'fingerprint',
];
/** Does this reporter's history warrant a careful call? */
export function needsCarefulHandling(r) {
    return r.fakeCount > 0;
}
/**
 * Has this reporter already been told what a further fabrication would mean?
 *
 * The Cell screen uses this to decide whether to prompt for the warning or to
 * show that it was already given. A referral to the FIA against somebody who
 * was never warned is not something this system should make easy.
 */
export function warningAlreadyGiven(r) {
    return r.lastWarningAt !== null;
}
/* -------------------------------------------------------------------------- */
/* The same video, twice                                                      */
/* -------------------------------------------------------------------------- */
/**
 * The deduplication key for a piece of footage.
 *
 * Built from {platform, videoId} and NOT from the URL, which is the whole
 * point: `youtu.be/abc`, `youtube.com/watch?v=abc&t=30`, and
 * `m.youtube.com/watch?v=abc&list=xyz` are three strings and one video.
 * Hashing the URL would let anybody file the same footage five times by adding
 * a query parameter, which is precisely the duplicate this is meant to catch.
 *
 * Used as a Firestore document id, so it must contain no slashes: platform is
 * from a fixed enum and videoId is already constrained to URL-safe characters
 * by the parser, so the join is safe.
 */
export function videoKey(video) {
    return `${video.platform}_${video.videoId}`;
}
/**
 * How many independent submissions make a ticket urgent.
 *
 * Two different people filming the same theft is the strongest signal the Cell
 * gets that something is really happening there, and it is close to unfakeable
 * from outside: a single person resubmitting their own footage raises the same
 * counter, but they have to do it from the same CNIC, which the Cell can see.
 */
export const CORROBORATION_THRESHOLD = 2;
export function isCorroborated(t) {
    return t.reportCount >= CORROBORATION_THRESHOLD;
}
/**
 * Validate a submission.
 *
 * Still deliberately generous about the REPORT — the Punjab bounds check is a
 * coarse box with margin, notes stay optional — because turning away a real
 * report is a worse failure than accepting a stray one.
 *
 * It is not generous about the REPORTER. Every identity field is required and
 * checked, because a half-filled identity is the worst of both worlds: the
 * citizen has given up their anonymity and the Cell still cannot telephone
 * them to verify anything.
 */
export function validateSubmission(input) {
    const name = input.reporterName?.trim() ?? '';
    if (name.length < MIN_NAME_LENGTH || name.length > MAX_NAME_LENGTH) {
        return { valid: false, reasonKey: 'report.error.nameRequired', field: 'reporterName' };
    }
    const father = input.reporterFatherName?.trim() ?? '';
    if (father.length < MIN_NAME_LENGTH || father.length > MAX_NAME_LENGTH) {
        return {
            valid: false,
            reasonKey: 'report.error.fatherNameRequired',
            field: 'reporterFatherName',
        };
    }
    if (!isValidCnicFormat(input.reporterCnic ?? '')) {
        return { valid: false, reasonKey: 'report.error.cnicInvalid', field: 'reporterCnic' };
    }
    if (!isValidPhone(input.reporterPhone ?? '')) {
        // The Cell verifies by telephoning. A number that cannot be dialled makes
        // the whole identity requirement pointless.
        return { valid: false, reasonKey: 'report.error.phoneInvalid', field: 'reporterPhone' };
    }
    const address = input.reporterAddress?.trim() ?? '';
    if (address.length < MIN_ADDRESS_LENGTH || address.length > MAX_ADDRESS_LENGTH) {
        return { valid: false, reasonKey: 'report.error.addressRequired', field: 'reporterAddress' };
    }
    if (!input.coordinates || !isWithinPunjab(input.coordinates)) {
        return {
            valid: false,
            reasonKey: 'report.error.coordinatesOutsidePunjab',
            field: 'coordinates',
        };
    }
    if (!input.videoUrl?.trim()) {
        return { valid: false, reasonKey: 'report.error.videoRequired', field: 'videoUrl' };
    }
    const video = parseVideoUrl(input.videoUrl);
    if (!video) {
        return { valid: false, reasonKey: 'report.error.videoNotRecognised', field: 'videoUrl' };
    }
    if ((input.notes?.length ?? 0) > MAX_NOTES_LENGTH) {
        return { valid: false, reasonKey: 'report.error.notesTooLong', field: 'notes' };
    }
    if ((input.locationHint?.length ?? 0) > MAX_LOCATION_HINT_LENGTH) {
        return { valid: false, reasonKey: 'report.error.locationHintTooLong', field: 'locationHint' };
    }
    if (!input.turnstileToken?.trim()) {
        return { valid: false, reasonKey: 'report.error.turnstileMissing', field: 'turnstile' };
    }
    return { valid: true, video };
}
/**
 * Strip anything that is not on the allow-list.
 *
 * Applied to the parsed request body BEFORE anything else touches it, so a
 * field somebody adds to the form — or an attacker appends to the JSON —
 * cannot ride through into storage. Allow-list, never block-list: a block-list
 * is one forgotten field away from being wrong.
 *
 * It now also NORMALISES the identity, here rather than at the call site. The
 * CNIC is the key to the reporter's history, and `42101-1234567-1` reaching
 * storage alongside `4210112345671` would split one person into two people
 * with clean records each.
 */
export function pickSubmissionFields(raw) {
    const str = (v, max) => typeof v === 'string' && v.trim() ? v.trim().slice(0, max) : undefined;
    const coords = raw.coordinates;
    const lat = typeof coords?.lat === 'number' ? coords.lat : undefined;
    const lng = typeof coords?.lng === 'number' ? coords.lng : undefined;
    const cnic = str(raw.reporterCnic, 20);
    const phone = str(raw.reporterPhone, 20);
    const normalisedPhone = phone ? normalisePhone(phone) : null;
    return {
        ...(str(raw.reporterName, MAX_NAME_LENGTH)
            ? { reporterName: str(raw.reporterName, MAX_NAME_LENGTH) }
            : {}),
        ...(str(raw.reporterFatherName, MAX_NAME_LENGTH)
            ? { reporterFatherName: str(raw.reporterFatherName, MAX_NAME_LENGTH) }
            : {}),
        ...(cnic ? { reporterCnic: normaliseCnic(cnic) } : {}),
        ...(normalisedPhone
            ? { reporterPhone: normalisedPhone }
            : phone
                ? { reporterPhone: phone }
                : {}),
        ...(str(raw.reporterAddress, MAX_ADDRESS_LENGTH)
            ? { reporterAddress: str(raw.reporterAddress, MAX_ADDRESS_LENGTH) }
            : {}),
        ...(lat !== undefined && lng !== undefined ? { coordinates: { lat, lng } } : {}),
        ...(str(raw.locationHint, MAX_LOCATION_HINT_LENGTH)
            ? { locationHint: str(raw.locationHint, MAX_LOCATION_HINT_LENGTH) }
            : {}),
        ...(str(raw.videoUrl, 2048) ? { videoUrl: str(raw.videoUrl, 2048) } : {}),
        ...(str(raw.notes, MAX_NOTES_LENGTH) ? { notes: str(raw.notes, MAX_NOTES_LENGTH) } : {}),
        ...(str(raw.turnstileToken, 4096) ? { turnstileToken: str(raw.turnstileToken, 4096) } : {}),
    };
}
/**
 * Round coordinates before storage.
 *
 * Five decimal places is about 1.1 m — finer than consumer GPS and finer than
 * anyone needs to find a cut in a canal bank. Storing the raw float would
 * preserve device-specific precision artefacts that are, in aggregate, a weak
 * fingerprint of the handset that sent them.
 */
export function roundCoordinates(p) {
    const r = (n) => Math.round(n * 1e5) / 1e5;
    return { lat: r(p.lat), lng: r(p.lng) };
}
/* -------------------------------------------------------------------------- */
/* Forwarding                                                                 */
/* -------------------------------------------------------------------------- */
export const MIN_CELL_SUMMARY = 40;
/**
 * May the Cell forward this ticket to a zone yet?
 *
 * The summary minimum is not bureaucracy. Once forwarded, the Cell member's
 * description is the ONLY thing the Chief Engineer and the SDO have to work
 * from — the video does not travel and neither does the citizen's account. A
 * ticket forwarded with "theft reported" wastes a site visit and, worse, gets
 * closed as nothing-found because the officer did not know what to look for.
 */
export function canForward(ticket) {
    if (ticket.state !== 'IDENTITY_VERIFIED' && ticket.state !== 'RETURNED_TO_CELL') {
        return { valid: false, reasonKey: 'report.error.notVerifiedYet' };
    }
    if ((ticket.cellSummary?.trim().length ?? 0) < MIN_CELL_SUMMARY) {
        return { valid: false, reasonKey: 'report.error.summaryTooShort' };
    }
    if (!ticket.zoneId) {
        return { valid: false, reasonKey: 'report.error.zoneRequired' };
    }
    return { valid: true };
}
/**
 * May the SDO mark this fabricated?
 *
 * A fabrication mark follows the reporter to their next call, so it carries
 * two requirements the other closures do not: a written reason, and a
 * reference to the recording the SDO made at the site. "I went and there was
 * nothing" is CLOSED_NO_THEFT; calling a citizen a liar needs the visit on
 * record.
 */
export function canMarkFake(input) {
    if (!input.reason?.trim() || input.reason.trim().length < MIN_CELL_SUMMARY) {
        return { valid: false, reasonKey: 'report.error.fakeReasonTooShort' };
    }
    if (!input.siteVisitReference?.trim()) {
        return { valid: false, reasonKey: 'report.error.siteVisitRequired' };
    }
    return { valid: true };
}
//# sourceMappingURL=publicReport.js.map